import { createContext, use, useCallback, useEffect, useRef } from "react";
import { useValue } from "@legendapp/state/react";
import {
  uiStateStore$,
  vocabularyExerciseStore$,
} from "@/ear-reader/state/stores";
import type { WordListItem } from "@bindery/shared/reader/page-parsing";
import type {
  VocabularyWord,
  WordStatus,
} from "@/ear-reader/types/vocabulary-exercise";

// Types

/**
 * Re-export VocabularyWord as Word for backward compatibility.
 * New code should prefer importing VocabularyWord directly from
 * @/lib/types/vocabulary-exercise.
 */
export type Word = VocabularyWord;

export type { WordListItem, WordStatus };

/**
 * Context-specific state shape that includes isDrawerOpen from uiStateStore$.
 * This differs from the store's VocabularyExerciseState by:
 * - Including isDrawerOpen (from uiStateStore$, not vocabularyExerciseStore$)
 * - Excluding focusMode (internal to the store, not exposed via context)
 */
export interface VocabularyExerciseContextState {
  wordList: Word[];
  activeIndex: number | null;
  reviewIndex: number | null;
  isDrawerOpen: boolean;
}

export interface VocabularyExerciseActions {
  setActiveIndex: (
    value: number | null | ((prev: number | null) => number | null),
  ) => void;
  setReviewIndex: (
    value: number | null | ((prev: number | null) => number | null),
  ) => void;
  onStatusChange: (id: number, newStatus: WordStatus) => void;
  markDisfluency: (index: number | null) => void;
}

export interface VocabularyExerciseContextValue {
  state: VocabularyExerciseContextState;
  actions: VocabularyExerciseActions;
}

// Context

export const VocabularyExerciseContext =
  createContext<VocabularyExerciseContextValue | null>(null);

/**
 * Hook to access vocabulary exercise state and actions.
 *
 * @throws {Error} If used outside of VocabularyExerciseProvider.
 *
 * @example
 * ```tsx
 * // Must be wrapped in VocabularyExerciseProvider
 * function MyComponent() {
 *   const { state, actions } = useVocabularyExercise();
 *   // ...
 * }
 * ```
 */
export function useVocabularyExercise(): VocabularyExerciseContextValue {
  const ctx = use(VocabularyExerciseContext);
  if (!ctx) {
    throw new Error(
      "useVocabularyExercise must be used within a VocabularyExerciseProvider",
    );
  }
  return ctx;
}

// Provider

export interface VocabularyExerciseProviderProps {
  children: React.ReactNode;
  onEngageWRF?: (word: WordListItem) => void;
}

/**
 * Provides vocabulary exercise state and actions via context.
 * Owns all store reads and mutations so child components can consume
 * a stable interface without directly importing global stores.
 *
 * Implementation notes:
 * - onEngageWRF is stored in a ref to keep callbacks stable across renders.
 *   This means the callback reference can change without causing context
 *   consumers to re-render, but the latest callback is always invoked.
 * - Actions are stable (useCallback with empty deps) and read current state
 *   via .peek() at invocation time, not at callback creation time.
 */
export function VocabularyExerciseProvider({
  children,
  onEngageWRF,
}: VocabularyExerciseProviderProps) {
  const wordList = useValue(vocabularyExerciseStore$.wordList);
  const activeIndex = useValue(vocabularyExerciseStore$.activeIndex);
  const reviewIndex = useValue(vocabularyExerciseStore$.reviewIndex);
  const isDrawerOpen = useValue(uiStateStore$.isDrawerOpen);

  // Keep onEngageWRF ref current so callbacks don't need it as a dependency.
  // This pattern ensures stable callback references while always calling
  // the latest version of onEngageWRF.
  const onEngageWRFRef = useRef(onEngageWRF);
  useEffect(() => {
    onEngageWRFRef.current = onEngageWRF;
  }, [onEngageWRF]);

  // --- Actions ---
  // All actions use the same pattern: stable callbacks (empty dependency arrays)
  // that read current state via .peek() at invocation time. External callbacks
  // like onEngageWRF are accessed via ref (above) so they can change without
  // invalidating the action callbacks. This prevents unnecessary re-renders
  // in memoized child components that receive these callbacks as props.

  const setActiveIndex = useCallback(
    (value: number | null | ((prev: number | null) => number | null)) => {
      const newValue =
        typeof value === "function"
          ? value(vocabularyExerciseStore$.activeIndex.peek())
          : value;
      vocabularyExerciseStore$.activeIndex.set(newValue);
    },
    [],
  );

  const setReviewIndex = useCallback(
    (value: number | null | ((prev: number | null) => number | null)) => {
      const newValue =
        typeof value === "function"
          ? value(vocabularyExerciseStore$.reviewIndex.peek())
          : value;
      vocabularyExerciseStore$.reviewIndex.set(newValue);
    },
    [],
  );

  const onStatusChange = useCallback((id: number, newStatus: WordStatus) => {
    // Read current state at invocation time, not at callback creation
    const currentWordList = vocabularyExerciseStore$.wordList.peek();

    if (newStatus === "unrecognized") {
      const selectedWord = currentWordList.find((w) => w.id === id);
      if (selectedWord) {
        onEngageWRFRef.current?.({
          id: selectedWord.id,
          text: selectedWord.text,
          slug: selectedWord.slug,
        });
      }
    }

    vocabularyExerciseStore$.wordList.set(
      currentWordList.map((w) =>
        w.id === id ? { ...w, status: newStatus } : w,
      ),
    );
  }, []);

  const markDisfluency = useCallback((index: number | null) => {
    // Read current state at invocation time
    const currentWordList = vocabularyExerciseStore$.wordList.peek();
    if (index === null || index < 0 || index >= currentWordList.length) return;
    const word = currentWordList[index];
    if (word?.status === "unreviewed") {
      onEngageWRFRef.current?.({
        id: word.id,
        text: word.text,
        slug: word.slug,
      });

      vocabularyExerciseStore$.wordList.set(
        currentWordList.map((w) =>
          w.id === word.id ? { ...w, status: "unrecognized" as const } : w,
        ),
      );
    }
  }, []);

  // State snapshot from store values — updates when any store value changes.
  const state: VocabularyExerciseContextState = {
    wordList,
    activeIndex,
    reviewIndex,
    isDrawerOpen,
  };

  // Actions are stable references (empty dependency arrays on every useCallback
  // above), so this object is reconstructed but its values never change identity.
  const actions: VocabularyExerciseActions = {
    setActiveIndex,
    setReviewIndex,
    onStatusChange,
    markDisfluency,
  };

  const contextValue = { state, actions };

  return (
    <VocabularyExerciseContext value={contextValue}>
      {children}
    </VocabularyExerciseContext>
  );
}
